import type { FeatureMiddleware } from '@cast/api-client';
import {
  demoNodeConstraints,
  demoClusterNodeSelectionList,
  demoClusterFilterInstanceTypes,
  demoAutoscalerFeatures,
  demoClusterRebalancingPlan,
  demoClusterRebalancingWorkloads,
  demoClusterGenerateRebalancingPlan,
  demoAdvancedEvictorConfig,
  demoPodMutationsList,
  demoPodMutation,
} from '@cast/fixtures';
import { PodMutatorStatusEnum } from '@cast/types-patching-engine';
import { DEMO_CLUSTER_ID } from '@cast/utils';

import {
  getIsClusterDemoMode,
  getIsOpDemoMode,
} from '@cast/infrastructure-demo';

import {
  rejectDemoClusterRequest,
  handleOpDemoInterceptor,
  isSimulatedDemoClusterRequest,
} from '../utils';

export const autoscaler: FeatureMiddleware<'autoscaler'> = {
  getNodeConstraints: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({});
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoNodeConstraints();
      return resolve(fixture);
    }
    return next();
  },
  getSelectionTemplates: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({ items: [] });
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoClusterNodeSelectionList();
      return resolve(fixture);
    }
    return next();
  },
  createSelectionTemplate: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  updateSelectionTemplate: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  deleteSelectionTemplate: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getInstanceTypes: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({ availableInstanceTypes: [] });
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoClusterFilterInstanceTypes();
      return resolve(fixture);
    }
    return next();
  },
  listRebalancingPlans: async ({ args, next, resolve }) => {
    if (
      getIsOpDemoMode() ||
      getIsClusterDemoMode() ||
      isSimulatedDemoClusterRequest(args[0])
    ) {
      // Intentionally empty until domain fixtures ship plan history for demo.
      return resolve({ items: [] });
    }
    return next();
  },
  getAutoscalerFeatures: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({
        evictorVersion: '',
        arm64Supported: false,
        reservationsEnabled: false,
        enableDefaultNodeTemplate: false,
        karpenterInstalled: false,
      });
    }
    if (getIsClusterDemoMode()) {
      return resolve(await demoAutoscalerFeatures());
    }
    return next();
  },
  getRebalancingPlan: async (controls) => {
    return handleOpDemoInterceptor({
      ...controls,
      demoClusterFallback: () =>
        demoClusterRebalancingPlan(controls.args[0].rebalancingPlanId)!,
    });
  },
  getWorkloads: async ({ next, resolve, feature, endpoint }) => {
    return handleOpDemoInterceptor({
      feature,
      endpoint,
      next,
      resolve,
      demoFixtureLoader: demoClusterRebalancingWorkloads,
      demoClusterFallback: async (args) =>
        isSimulatedDemoClusterRequest(args[0])
          ? await demoClusterRebalancingWorkloads()
          : {
              clusterId: DEMO_CLUSTER_ID,
              workloads: [],
            },
    });
  },
  generateRebalancingPlan: async ({ args, next, resolve }) => {
    if (getIsOpDemoMode()) {
      return rejectDemoClusterRequest();
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoClusterGenerateRebalancingPlan(
        args[0].body.rebalancingNodes?.map((n) => n.nodeId!) || []
      );
      return resolve(fixture);
    }
    return next();
  },
  executeRebalancingPlan: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  updateAdvancedEvictorConfig: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getAdvancedEvictorConfig: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({ evictionConfig: [] });
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoAdvancedEvictorConfig();
      return resolve(fixture);
    }
    return next();
  },
  getPodMutations: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({ items: [] });
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoPodMutationsList();
      return resolve(fixture);
    }
    return next();
  },
  getPodMutation: async ({ args, next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({});
    }
    if (getIsClusterDemoMode()) {
      const fixture = await demoPodMutation(args[0].id);
      return resolve(fixture);
    }
    return next();
  },
  createPodMutation: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  updatePodMutation: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  deletePodMutation: async ({ next }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getMutatorStatus: async ({ next, resolve }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return resolve({
        status: PodMutatorStatusEnum.RUNNING,
      });
    }
    return next();
  },
  getProblematicNodes: (args) =>
    handleOpDemoInterceptor({
      ...args,
      opDemoFallback: {
        clusterId: DEMO_CLUSTER_ID,
        hasProblems: false,
        nodes: [],
      },
    }),
};
